import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styles from './index.module.scss'
import TaskService from "../../../../../../services/task.service";
import CommentService from "../../../../../../services/comment.service";
import AttachmentService from "../../../../../../services/attachment.service";
import UserService from "../../../../../../services/user.service";
import Avatar from "../../../../../Avatar";
import InfoTooltip from "../../../../../InfoTooltip";
import {useUser} from "cor-business";
import CommentInput, {ChipMenu, CommentLink, CommentMention, getLinkIcon, parseUrl} from "../CommentInput";
import CommentMarkdown from "../../../../../CommentMarkdown";
import EditIcon from "../../../../../../assets/icon/EditIcon";
import TrashIcon from "../../../../../../assets/icon/TrashIcon";
import {
    fetchAttachmentObjectUrl,
    fetchAttachmentPreviewObjectUrl,
    isImg,
} from "../../../../../../helpers/attachmentImage";
import ProjectDeleteModal from '../../../../../ProjectControls/ProjectDeleteModal';
import { isCommentOwnedByUser } from '../../../../../../utils/commentOwnership';
import { dispatchProjectNotification } from '../../../../../../utils/projectNotification';
import { getCommentLikeState } from '../../../../../../utils/commentLikes';
import { usersHaveSameIdentity } from '../../../../../../utils/members';
import {
    buildCommentThread,
    buildCommentThreadRails,
    getCommentParentId,
    stripCommentReplyMarker,
} from '../../../../../../utils/commentThreads';
import {
    prepareCommentContentForCreate,
    serializeCommentContent,
    stripAttachmentOnlyCommentContent,
} from '../../../../../../utils/commentContent';

// Comment attachments are served from an auth-protected endpoint, so a bare
// <img src={file_url}> gets a 401. Load the blob through the authorized helper
// and render the resulting object-URL, revoking it on unmount.
function AttachmentRemoveIcon() {
    return (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    )
}

function CommentAttachment({ attachment, onRemove }: { attachment: any; onRemove?: () => void }) {
    const [src, setSrc] = useState<string | null>(null)
    const isImage = isImg(attachment?.filename || attachment?.file_url || '')

    useEffect(() => {
        let objectUrl: string | null = null
        let cancelled = false
        const loadObjectUrl = isImage
            ? fetchAttachmentObjectUrl(attachment.file_url)
            : fetchAttachmentPreviewObjectUrl(
                attachment.file_url,
                attachment.filename || '',
            )

        loadObjectUrl.then((url) => {
            if (cancelled) {
                if (url?.startsWith('blob:') && typeof URL.revokeObjectURL === 'function') {
                    URL.revokeObjectURL(url)
                }
                return
            }
            objectUrl = url
            setSrc(url)
        })
        return () => {
            cancelled = true
            if (objectUrl?.startsWith('blob:') && typeof URL.revokeObjectURL === 'function') {
                URL.revokeObjectURL(objectUrl)
            }
        }
    }, [attachment?.file_url, attachment?.filename, isImage])

    const preview = (
        <a
            href={src || undefined}
            target="_blank"
            rel="noopener noreferrer"
            download={!isImage ? (attachment.filename || 'file') : undefined}
            onClick={(e) => { if (!src) e.preventDefault() }}
            className={styles.actions__attachment}
            title={attachment.filename}
        >
            {isImage && src ? (
                <img src={src} alt={attachment.filename} />
            ) : (
                <span>{attachment.filename || 'Файл'}</span>
            )}
        </a>
    )

    if (!onRemove) return preview

    return (
        <div className={styles.actions__attachmentEditable}>
            {preview}
            <button
                type="button"
                className={styles.actions__attachmentRemove}
                aria-label={`Удалить вложение ${attachment.filename || 'Файл'}`}
                title="Удалить вложение"
                onClick={onRemove}
            >
                <AttachmentRemoveIcon />
            </button>
        </div>
    )
}

const isCommentActivity = (activity: any) => {
    const type = String(activity?.action_type || '').toLowerCase()
    return type.includes('comment')
}

interface ParsedComment {
    text: string
    links: CommentLink[]
    mentions: { name: string }[]
}

// reverse of buildCommentBody — split a comment body back into text / links / mentions
const parseCommentContent = (content: string): ParsedComment => {
    const lines = stripAttachmentOnlyCommentContent(stripCommentReplyMarker(content)).split('\n')
    const textLines: string[] = []
    const links: CommentLink[] = []
    const mentions: { name: string }[] = []

    lines.forEach((rawLine) => {
        const line = rawLine.trim()
        if (!line) {
            textLines.push(rawLine)
            return
        }
        // mention line: "@Имя Фамилия" (no URL on the line)
        if (line.startsWith('@') && !/https?:\/\//i.test(line)) {
            mentions.push({ name: line.slice(1).trim() })
            return
        }
        // "Title: https://..."
        const titledMatch = line.match(/^(.+?):\s*(https?:\/\/[^\s]+)$/i)
        if (titledMatch) {
            links.push({ title: titledMatch[1].trim(), url: titledMatch[2].trim() })
            return
        }
        // bare url line
        if (/^https?:\/\/[^\s]+$/i.test(line)) {
            links.push({ url: line })
            return
        }
        textLines.push(rawLine)
    })

    return { text: textLines.join('\n').trim(), links, mentions }
}

// ── footer icons ──────────────────────────────────────────────────────────────
const LikeIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.18421 20H6.02632V9.05263H5.18421C4.73753 9.05263 4.30914 9.23007 3.99329 9.54593C3.67744 9.86178 3.5 10.2902 3.5 10.7368V18.3158C3.5 18.7625 3.67744 19.1909 3.99329 19.5067C4.30914 19.8226 4.73753 20 5.18421 20ZM18.6579 9.05263H12.7632L13.708 6.21642C13.7923 5.9633 13.8153 5.69376 13.775 5.43003C13.7347 5.16629 13.6324 4.91589 13.4764 4.69945C13.3204 4.48301 13.1152 4.30673 12.8778 4.18512C12.6403 4.06352 12.3773 4.00007 12.1105 4H11.9211L7.71053 8.57937V20H16.3301C16.7221 20 17.0779 19.771 17.2403 19.4142L20.268 12.7613L20.3421 12.4211V10.7368C20.3421 10.2902 20.1647 9.86178 19.8488 9.54593C19.533 9.23007 19.1046 9.05263 18.6579 9.05263Z" fill="currentColor"/>
    </svg>
)

const getLikedUserName = (likedUser: any) => {
    const nestedUser = likedUser?.user || likedUser?.profile || {}
    const directName = [likedUser?.first_name, likedUser?.last_name].filter(Boolean).join(' ')
    const nestedName = [nestedUser?.first_name, nestedUser?.last_name].filter(Boolean).join(' ')

    return directName
        || nestedName
        || likedUser?.full_name
        || nestedUser?.full_name
        || likedUser?.name
        || nestedUser?.name
        || likedUser?.email
        || nestedUser?.email
        || 'Пользователь'
}

const CommentLikeUsers = ({ users }: { users: any[] }) => {
    if (users.length === 0) return null

    return (
        <div className={styles.actions__comment_likeUsers} aria-label="Пользователи, которым понравился комментарий">
            {users.map((likedUser, index) => {
                const userName = getLikedUserName(likedUser)
                const userKey = likedUser?.id ?? likedUser?.user_id ?? likedUser?.email ?? index

                return (
                    <InfoTooltip key={String(userKey)} content={userName}>
                        <div
                            className={styles.actions__comment_likeUser}
                            aria-label={userName}
                            style={{ zIndex: users.length - index }}
                        >
                            <Avatar user={likedUser}/>
                        </div>
                    </InfoTooltip>
                )
            })}
        </div>
    )
}

const getLikedByUsers = (comment: any) => (
    Array.isArray(comment?.liked_by_users) ? comment.liked_by_users : []
)

const CommentLikeAction = React.memo(function CommentLikeAction({
    comment,
    user,
    optimisticUser,
}: {
    comment: any
    user: any
    optimisticUser?: any
}) {
    const initialLikeState = getCommentLikeState(comment, user)
    const [view, setView] = useState(() => ({
        ...initialLikeState,
        users: getLikedByUsers(comment),
        pending: false,
    }))
    const pendingRef = useRef(false)
    const awaitingServerLikeStateRef = useRef<boolean | null>(null)

    useEffect(() => {
        if (pendingRef.current) return
        const nextState = getCommentLikeState(comment, user)

        // A comments poll that started before the mutation can finish after it
        // and carry the old reaction. Ignore that snapshot until polling catches
        // up with the state confirmed by POST/DELETE.
        if (
            awaitingServerLikeStateRef.current !== null
            && nextState.liked !== awaitingServerLikeStateRef.current
        ) {
            return
        }

        if (nextState.liked === awaitingServerLikeStateRef.current) {
            awaitingServerLikeStateRef.current = null
        }
        setView({ ...nextState, users: getLikedByUsers(comment), pending: false })
    }, [comment, user])

    const handleToggle = async () => {
        const commentId = Number(comment?.id)
        if (!Number.isFinite(commentId) || pendingRef.current) return

        const previousView = view
        const nextLiked = !view.liked
        const nextUsers = nextLiked && optimisticUser
            ? (view.users.some((likedUser: any) => usersHaveSameIdentity(likedUser, optimisticUser))
                ? view.users
                : [...view.users, optimisticUser])
            : (!nextLiked && optimisticUser
                ? view.users.filter((likedUser: any) => !usersHaveSameIdentity(likedUser, optimisticUser))
                : view.users)

        pendingRef.current = true
        awaitingServerLikeStateRef.current = nextLiked
        setView({
            liked: nextLiked,
            count: Math.max(0, view.count + (view.liked ? -1 : 1)),
            users: nextUsers,
            pending: true,
        })

        try {
            const result = nextLiked
                ? await CommentService.like(commentId)
                : await CommentService.unlike(commentId)
            const responseComment = result?.comment ?? result?.data ?? result
            const hasLikePayload = responseComment && typeof responseComment === 'object' && (
                'liked_by_current_user' in responseComment
                || 'likes_count' in responseComment
                || 'liked_by_users' in responseComment
            )

            if (hasLikePayload) {
                const responseState = getCommentLikeState(responseComment, user)
                const responseUsers = Array.isArray(responseComment?.liked_by_users)
                    ? responseComment.liked_by_users
                    : nextUsers
                const stableResponseUsers = nextLiked && optimisticUser
                    ? (responseUsers.some((likedUser: any) => usersHaveSameIdentity(likedUser, optimisticUser))
                        ? responseUsers
                        : [...responseUsers, optimisticUser])
                    : (!nextLiked && optimisticUser
                        ? responseUsers.filter((likedUser: any) => !usersHaveSameIdentity(likedUser, optimisticUser))
                        : responseUsers)
                setView({
                    ...responseState,
                    liked: nextLiked,
                    users: stableResponseUsers,
                    pending: true,
                })
            }
        } catch (e) {
            awaitingServerLikeStateRef.current = null
            setView({ ...previousView, pending: true })
            dispatchProjectNotification({
                text: 'Не удалось обновить реакцию. Попробуйте ещё раз.',
                type: 'error',
            })
            console.error('Failed to toggle comment like:', e)
        } finally {
            pendingRef.current = false
            setView((current) => ({ ...current, pending: false }))
        }
    }

    return (
        <>
            <button
                type="button"
                className={`${styles.actions__comment_action} ${view.liked ? styles.actions__comment_actionActive : ''}`}
                title={view.liked ? 'Прибрати вподобання' : 'Подобається'}
                aria-label={view.liked ? 'Прибрати вподобання' : 'Подобається'}
                aria-pressed={view.liked}
                aria-disabled={view.pending}
                onClick={handleToggle}
            >
                <LikeIcon />
            </button>
            <CommentLikeUsers users={view.users}/>
        </>
    )
})

export default function Actions({ taskId, projectId, refreshVersion = 0, targetCommentId }: any){
    const {user} = useUser()
    const [activities, setActivities] = useState<any[]>([])
    const [comments, setComments] = useState<any[]>([])
    const [commentsStatus, setCommentsStatus] = useState<'loading' | 'loaded' | 'error'>('loading')
    const [activitiesLoaded, setActivitiesLoaded] = useState(false)
    const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null)
    const timelineRef = useRef<HTMLDivElement>(null)
    const handledCommentRef = useRef<string | null>(null)
    const linkedCommentId = /^[1-9]\d*$/.test(String(targetCommentId ?? '')) ? String(targetCommentId) : null
    const [usersById, setUsersById] = useState<Record<string, any>>({})
    const [editingCommentId, setEditingCommentId] = useState<number | null>(null)
    const [editingCommentLinkIdx, setEditingCommentLinkIdx] = useState<number | null>(null)
    const [editingCommentAttachments, setEditingCommentAttachments] = useState<any[]>([])
    const [replyingToId, setReplyingToId] = useState<number | null>(null)
    const [commentToDelete, setCommentToDelete] = useState<any | null>(null)
    const knownUsers = useMemo(() => Array.from(new Set(Object.values(usersById))), [usersById])
    const currentProjectUser = useMemo(
        () => knownUsers.find((knownUser) => usersHaveSameIdentity(knownUser, user)) || null,
        [knownUsers, user],
    )
    const activitiesRequestRef = useRef(0)
    const commentsRequestRef = useRef(0)

    const startEditComment = (comment: any, linkIdx: number | null = null) => {
        setReplyingToId(null)
        setEditingCommentLinkIdx(linkIdx)
        setEditingCommentAttachments(Array.isArray(comment?.attachments) ? comment.attachments : [])
        setEditingCommentId(Number(comment.id))
    }

    const stopEditComment = () => {
        setEditingCommentId(null)
        setEditingCommentLinkIdx(null)
        setEditingCommentAttachments([])
    }

    const getActivities = useCallback(async () => {
        const requestId = ++activitiesRequestRef.current

        try {
            const activitiesList = await TaskService.activities(taskId)
            if (requestId === activitiesRequestRef.current) {
                setActivities(Array.isArray(activitiesList?.activities) ? activitiesList.activities : [])
            }
        } catch (e) {
            console.error('Failed to load activities:', e)
        } finally {
            if (requestId === activitiesRequestRef.current) setActivitiesLoaded(true)
        }
    }, [taskId])

    const getComments = useCallback(async () => {
        const requestId = ++commentsRequestRef.current

        try {
            const list = await CommentService.listByTask(taskId)
            const items = Array.isArray(list) ? list : (Array.isArray(list?.comments) ? list.comments : [])
            if (requestId === commentsRequestRef.current) {
                setComments(items)
                setCommentsStatus('loaded')
            }
        } catch (e) {
            console.error('Failed to load comments:', e)
            if (requestId === commentsRequestRef.current) setCommentsStatus('error')
        }
    }, [taskId])

    const refreshTimeline = useCallback(async () => {
        await Promise.all([getActivities(), getComments()])
    }, [getActivities, getComments])

    const loadUsers = useCallback(async () => {
        try {
            const list = await UserService.getUsers({
                skip: 0,
                limit: 1000,
                ...(projectId ? { project_id: Number(projectId) } : {}),
            })
            if (Array.isArray(list)) {
                const map: Record<string, any> = {}
                list.forEach((u: any) => {
                    [u?.id, u?.user_id, u?.corid, u?.cor_id]
                        .filter((id) => id !== undefined && id !== null && String(id) !== '')
                        .forEach((id) => {
                            map[String(id)] = u
                        })
                })
                setUsersById(map)
            }
        } catch (e) {
            console.error('Failed to load users for comments:', e)
        }
    }, [projectId])

    useEffect(() => {
        void refreshTimeline()
    }, [refreshTimeline])

    useEffect(() => {
        if (refreshVersion > 0) void getActivities()
    }, [getActivities, refreshVersion])

    useEffect(() => {
        void loadUsers()
    }, [loadUsers])

    useEffect(() => {
        const refreshVisibleTimeline = () => {
            if (document.visibilityState === 'visible') {
                void refreshTimeline()
            }
        }
        const commentsIntervalId = window.setInterval(() => {
            if (document.visibilityState === 'visible') void getComments()
        }, 60000)
        const activitiesIntervalId = window.setInterval(() => {
            if (document.visibilityState === 'visible') void getActivities()
        }, 60000)

        window.addEventListener('focus', refreshVisibleTimeline)
        document.addEventListener('visibilitychange', refreshVisibleTimeline)

        return () => {
            window.clearInterval(commentsIntervalId)
            window.clearInterval(activitiesIntervalId)
            window.removeEventListener('focus', refreshVisibleTimeline)
            document.removeEventListener('visibilitychange', refreshVisibleTimeline)
        }
    }, [getActivities, getComments, refreshTimeline])

    const getInfo = (activity: any): { key: string, value: string } | null => {
        const getValue = (oldValue: any, newValue: any) => {
            let result = []
            if(oldValue){
                result.push(oldValue)
            }
            if(newValue){
                result.push(newValue)
            }

            return result.join(" ▶ ")
        }

        if(activity.action_type === "created"){
            return {
                key: "Создал(а) эту задачу",
                value: ""
            }
        }
        const newValue = activity.new_value || {}
        const oldValue = activity.old_value || {}
        const field = newValue.field || oldValue.field

        if(activity.action_type === "status_changed"){
            const oldStatus = oldValue.status_name || oldValue.status || oldValue.value
            const newStatus = newValue.status_name || newValue.status || newValue.value

            return {
                key: "Изменил(а) статус:",
                value: getValue(oldStatus, newStatus)
            }
        }

        if(activity.action_type === "time_logged"){
            const minutesSpent = Number(newValue.minutes_spent)
            const duration = newValue.minutes_spent_label
                || (newValue.minutes_spent != null && Number.isFinite(minutesSpent) ? `${minutesSpent} мин.` : "")
            const description = String(newValue.description || '').trim()

            return {
                key: "Записал(а) затраченное время:",
                value: [duration, description].filter(Boolean).join(" — ")
            }
        }

        if(activity.action_type === "updated"){
            if(field === "due_date"){
                return {
                    key: "Изменил(а) Срок:",
                    value: getValue(oldValue.value && new Date(oldValue.value).toLocaleDateString(), newValue.value && new Date(newValue.value).toLocaleDateString())
                }
            }
            if(field === "priority"){
                return {
                    key: "Изменил(а) Приоритет:",
                    value: getValue(oldValue.value, newValue.value)
                }
            }
            if(field === "title"){
                return {
                    key: "Изменил(а) Название:",
                    value: getValue(oldValue.value, newValue.value)
                }
            }
            if(field === "description"){
                return {
                    key: "Изменил(а) описание задачи",
                    value: ""
                }
            }
            if(field === "task_type"){
                return {
                    key: "Изменил(а) тип задачи:",
                    value: getValue(oldValue.value, newValue.value)
                }
            }
            if(field === "assignee_ids" || field === "assignees"){
                return {
                    key: "Изменил(а) участников задачи",
                    value: ""
                }
            }
            if(field === "subtask_created"){
                return {
                    key: "Создал(а) подзадачу:",
                    value: getValue(newValue.subtask_title, null)
                }
            }

            return {
                key: "Изменил(а) задачу:",
                value: field || ""
            }
        }

        if(activity.action_type === "attachment_added"){
            return {
                key: "Добавили вложение: ",
                value: newValue.filename || ""
            }
        }

        if(activity.action_type === "attachment_deleted" || activity.action_type === "attachment_removed"){
            return {
                key: "Удалили вложение: ",
                value: oldValue.filename || newValue.filename || ""
            }
        }

        if(activity.action_type === "assigned"){
            return {
                key: "Добавили исполнителя: ",
                value: (newValue.assignees || []).map((u: any) => `${u.first_name} ${u.last_name}`).join(", ")
            }
        }

        if(activity.action_type === "unassigned"){
            return {
                key: "Удалил(а) исполнителя: ",
                value: (oldValue.assignees || newValue.assignees || [])
                    .map((u: any) => `${u.first_name || ''} ${u.last_name || ''}`.trim())
                    .filter(Boolean)
                    .join(", ")
            }
        }

        const actionLabel = String(activity.action_type || '')
            .replace(/_/g, ' ')
            .trim()

        return actionLabel
            ? { key: "Выполнил(а) действие:", value: actionLabel }
            : null
    }

    const getActivityUser = (activity: any) => ({
        first_name: activity.activitiesuser_first_name || activity.user_first_name || activity.user?.first_name || '',
        last_name: activity.activitiesuser_last_name || activity.user_last_name || activity.user?.last_name || '',
        name: activity.user_name || activity.activitiesuser_name || activity.user?.name || activity.new_value?.logged_by_name || '',
        avatar: activity.avatar || activity.user_avatar || activity.activitiesuser_avatar || activity.user?.avatar,
        photo: activity.photo || activity.user_photo || activity.activitiesuser_photo || activity.user?.photo,
        photo_data_url: activity.photo_data_url || activity.user_photo_data_url || activity.activitiesuser_photo_data_url || activity.user?.photo_data_url,
        avatar_url: activity.avatar_url || activity.user_avatar_url || activity.activitiesuser_avatar_url || activity.user?.avatar_url,
        image: activity.image || activity.user_image || activity.activitiesuser_image || activity.user?.image,
    })

    const getCommentUser = (comment: any) => {
        const fromMap = comment?.user_id !== undefined ? usersById[String(comment.user_id)] : undefined
        if (fromMap) return fromMap
        if (comment?.user) return comment.user
        if (isCommentOwnedByUser(comment, user)) {
            return user
        }
        return { name: 'Пользователь' }
    }

    const getCommentUserName = (commentUser: any) =>
        `${commentUser?.first_name || ''} ${commentUser?.last_name || ''}`.trim()
        || commentUser?.name
        || commentUser?.email
        || 'Пользователь'

    const handleCreateComment = async ({ text, links, mentions, files }: { text: string; links: CommentLink[]; mentions: CommentMention[]; files: File[] }, parentCommentId?: number) => {
        const content = serializeCommentContent(text, links, mentions, parentCommentId)
        if (!content && files.length === 0) return
        const created = await CommentService.create({
            task_id: Number(taskId),
            content: prepareCommentContentForCreate(content, files.length > 0),
            ...(parentCommentId ? { parent_comment_id: parentCommentId } : {}),
        })
        const newCommentId = created?.id ?? created?.comment?.id
        if (files.length > 0 && !newCommentId) {
            throw new Error('Сервер не подтвердил создание комментария. Вложения не загружены.')
        }
        if (files.length > 0) {
            try {
                await CommentService.uploadAttachments(Number(newCommentId), files)
            } catch (e) {
                try {
                    await CommentService.delete(Number(newCommentId))
                } catch (rollbackError) {
                    console.error('Failed to roll back comment after attachment upload error:', rollbackError)
                }
                throw e
            }
        }

        setReplyingToId(null)
        await refreshTimeline()
    }

    const handleUpdateComment = async (comment: any, { text, links, mentions, files }: { text: string; links: CommentLink[]; mentions: CommentMention[]; files: File[] }) => {
        const commentId = Number(comment.id)
        const parentCommentId = getCommentParentId(comment)
        const content = serializeCommentContent(text, links, mentions, parentCommentId)
        const originalAttachments = Array.isArray(comment.attachments) ? comment.attachments : []
        const retainedIds = new Set(editingCommentAttachments.map((attachment) => String(attachment.id)))
        const removedAttachments = originalAttachments.filter((attachment: any) => !retainedIds.has(String(attachment.id)))
        const hasAttachmentsAfterSave = editingCommentAttachments.length > 0 || files.length > 0
        const preparedContent = prepareCommentContentForCreate(content, hasAttachmentsAfterSave)

        if (!preparedContent) {
            if (removedAttachments.length > 0) {
                await CommentService.delete(commentId)
                stopEditComment()
                await refreshTimeline()
            }
            return
        }

        await CommentService.update(commentId, preparedContent, parentCommentId)
        if (files.length > 0) {
            await CommentService.uploadAttachments(commentId, files)
        }
        if (removedAttachments.length > 0) {
            await Promise.all(removedAttachments.map((attachment: any) => AttachmentService.delete(attachment.id)))
        }
        stopEditComment()
        await refreshTimeline()
    }

    const handleDeleteComment = async () => {
        if (!commentToDelete?.id) return

        try {
            await CommentService.delete(commentToDelete.id)
            if (editingCommentId === commentToDelete.id) {
                stopEditComment()
            }
            setCommentToDelete(null)
            await refreshTimeline()
        } catch (e) {
            console.error('Failed to delete comment:', e)
        }
    }

    // remove a single link from a comment body and save
    const handleRemoveCommentLink = async (comment: any, linkIdx: number) => {
        const parsed = parseCommentContent(comment.content || '')
        const nextLinks = parsed.links.filter((_, i) => i !== linkIdx)
        const content = serializeCommentContent(
            parsed.text,
            nextLinks,
            parsed.mentions.map((m) => ({ id: m.name, name: m.name })),
            getCommentParentId(comment),
        )
        try {
            if (!content) {
                await CommentService.delete(comment.id)
            } else {
                await CommentService.update(comment.id, content, getCommentParentId(comment))
            }
            await refreshTimeline()
        } catch (e) {
            console.error('Failed to update comment link:', e)
        }
    }

    const timeline = useMemo(() => {
        const activityItems = activities
            .filter((activity) => !isCommentActivity(activity))
            .map((activity) => ({
                kind: 'activity' as const,
                id: `activity-${activity.id || `${activity.created_at}-${activity.action_type}`}`,
                createdAt: activity.created_at,
                data: activity,
            }))

        const commentItems = buildCommentThread(comments).map(({ comment, parent, depth, rootCreatedAt, threadOrder }) => ({
            kind: 'comment' as const,
            id: `comment-${comment.id}`,
            createdAt: rootCreatedAt,
            threadOrder,
            depth,
            parent,
            data: comment,
        }))

        return [...activityItems, ...commentItems].sort((a, b) => {
            const aTime = new Date(a.createdAt).getTime() || 0
            const bTime = new Date(b.createdAt).getTime() || 0
            if (aTime !== bTime) return aTime - bTime
            if (a.kind === 'comment' && b.kind === 'comment') return a.threadOrder - b.threadOrder
            return 0
        })
    }, [activities, comments])

    const groupedTimeline = useMemo(() => {
        const groups: any[] = []
        let currentThread: any | null = null

        timeline.forEach((item) => {
            if (item.kind === 'activity') {
                currentThread = null
                groups.push(item)
                return
            }

            if (item.depth === 0 || !currentThread) {
                currentThread = {
                    kind: 'commentThread' as const,
                    id: `thread-${item.id}`,
                    items: [item],
                }
                groups.push(currentThread)
                return
            }

            currentThread.items.push(item)
        })

        return groups.map((group) => (
            group.kind === 'commentThread'
                ? { ...group, rails: buildCommentThreadRails(group.items) }
                : group
        ))
    }, [timeline])

    useEffect(() => {
        handledCommentRef.current = null
        setHighlightedCommentId(null)
    }, [linkedCommentId])

    useEffect(() => {
        if (!linkedCommentId || commentsStatus !== 'loaded' || !activitiesLoaded
            || handledCommentRef.current === linkedCommentId) return

        const scrollContainer = timelineRef.current
        const target = scrollContainer?.querySelector<HTMLElement>(`[data-comment-id="${linkedCommentId}"]`)
        if (!scrollContainer || !target) return

        // Wait until the loaded timeline has been laid out. Scroll only this
        // sidebar so the task description and the board keep their positions.
        const frame = window.requestAnimationFrame(() => {
            const targetRect = target.getBoundingClientRect()
            const containerRect = scrollContainer.getBoundingClientRect()
            const top = scrollContainer.scrollTop + targetRect.top - containerRect.top
                - Math.max(0, (scrollContainer.clientHeight - targetRect.height) / 2)
            const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
            scrollContainer.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' })
            target.focus({ preventScroll: true })
            handledCommentRef.current = linkedCommentId
            setHighlightedCommentId(linkedCommentId)
        })

        return () => window.cancelAnimationFrame(frame)
    }, [activitiesLoaded, commentsStatus, groupedTimeline, linkedCommentId])

    useEffect(() => {
        if (!highlightedCommentId) return
        const timeout = window.setTimeout(() => setHighlightedCommentId(null), 5000)
        return () => window.clearTimeout(timeout)
    }, [highlightedCommentId])

    const linkedCommentMissing = linkedCommentId && commentsStatus === 'loaded'
        && !comments.some((comment) => String(comment.id) === linkedCommentId)

    return (
        <div className={styles.actions}>
            <div className={styles.actions__scroll} ref={timelineRef}>
                <div className={styles.actions__title}>Действия участников</div>
                {linkedCommentId && (linkedCommentMissing || commentsStatus === 'error') && (
                    <div className={styles.actions__linkNotice} role="status">
                        {commentsStatus === 'error'
                            ? 'Не удалось загрузить комментарий по ссылке. Попробуйте обновить страницу.'
                            : 'Комментарий по ссылке не найден. Возможно, он был удалён.'}
                    </div>
                )}
                <div>
                    {groupedTimeline.map((group) => {
                        if (group.kind === 'activity') {
                            const activity = group.data

                            const activityInfo = getInfo(activity)

                            if (!activityInfo || (!activityInfo.key && !activityInfo.value)) {
                                return null
                            }

                            const activityUser = getActivityUser(activity)
                            return (
                                <div className={styles.actions__item} key={group.id}>
                                    <div className={styles.actions__item_avatar}>
                                        <Avatar user={activityUser}/>
                                    </div>
                                    <div style={{flex: 1}}>
                                        <div className={styles.actions__item_top}>
                                            <div className={styles.actions__item_name}>
                                                {`${activityUser.first_name} ${activityUser.last_name}`.trim() || activityUser.name}
                                            </div>
                                            <div className={styles.actions__item_date}>{new Date(activity.created_at).toLocaleString()}</div>
                                        </div>
                                        <div className={styles.actions__item_description}>
                                            {activityInfo.key} {activityInfo.value}
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        const threadItems = group.items
                        const isThreaded = threadItems.length > 1

                        return (
                            <div
                                className={isThreaded ? styles.actions__comment_thread : undefined}
                                key={group.id}
                                data-comment-thread={isThreaded ? group.id : undefined}
                            >
                                {threadItems.map((item: any, itemIndex: number) => {
                                    const comment = item.data
                                    const commentUser = getCommentUser(comment)
                                    const isOwn = isCommentOwnedByUser(comment, user, knownUsers)
                                    const userName = getCommentUserName(commentUser)
                                    const parsed = parseCommentContent(comment.content || '')
                                    const { hasNextSibling, continuationDepths } = group.rails[itemIndex]

                                    return (
                                        <div
                                            className={`${styles.actions__comment} ${item.depth > 0 ? styles.actions__comment_replyItem : ''} ${isThreaded && item.depth === 0 ? styles.actions__comment_threadRoot : ''} ${hasNextSibling ? styles.actions__comment_replyHasNext : ''} ${highlightedCommentId === String(comment.id) ? styles.actions__comment_highlighted : ''}`}
                                            key={item.id}
                                            id={`comment-${comment.id}`}
                                            tabIndex={-1}
                                            data-comment-id={comment.id}
                                            data-highlighted={highlightedCommentId === String(comment.id) ? 'true' : undefined}
                                            data-comment-depth={item.depth}
                                            data-has-next-sibling={hasNextSibling ? 'true' : undefined}
                                            style={{ marginLeft: `${Math.min(item.depth, 3) * 32}px` }}
                                        >
                                            {continuationDepths.map((ancestorDepth) => (
                                                <span
                                                    key={`rail-${ancestorDepth}`}
                                                    className={styles.actions__comment_continuationRail}
                                                    aria-hidden="true"
                                                    data-continuation-depth={ancestorDepth}
                                                    style={{ left: `${(ancestorDepth - item.depth) * 32 - 17}px` }}
                                                />
                                            ))}
                                            <div className={styles.actions__comment_head}>
                                                <div className={styles.actions__comment_avatar}>
                                                    <Avatar user={commentUser}/>
                                                </div>
                                                <div className={styles.actions__comment_author}>{userName}</div>
                                                <div className={styles.actions__comment_date}>
                                                    {new Date(comment.created_at).toLocaleDateString()} в {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>

                                            {editingCommentId === comment.id ? (
                                                <CommentInput
                                                    projectId={projectId}
                                                    parentCommentId={getCommentParentId(comment) || undefined}
                                                    autoExpand
                                                    submitLabel="save"
                                                    initialValue={parsed.text}
                                                    initialLinks={parsed.links}
                                                    initialMentions={parsed.mentions.map((m) => ({ id: m.name, name: m.name }))}
                                                    existingAttachmentsCount={editingCommentAttachments.length}
                                                    hasExistingAttachmentChanges={editingCommentAttachments.length !== (Array.isArray(comment.attachments) ? comment.attachments.length : 0)}
                                                    existingAttachments={editingCommentAttachments.length > 0 ? (
                                                        <div className={`${styles.actions__attachments} ${styles.actions__attachmentsEditing}`}>
                                                            {editingCommentAttachments.map((attachment: any) => (
                                                                <CommentAttachment
                                                                    key={attachment.id}
                                                                    attachment={attachment}
                                                                    onRemove={() => setEditingCommentAttachments((current) => (
                                                                        current.filter((item) => String(item.id) !== String(attachment.id))
                                                                    ))}
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                    autoEditLinkIdx={editingCommentLinkIdx}
                                                    placeholder="Изменить комментарий"
                                                    onSubmit={(payload) => handleUpdateComment(comment, payload)}
                                                    onCancel={stopEditComment}
                                                />
                                            ) : (
                                                <>
                                                    {parsed.text && (
                                                        <CommentMarkdown className={styles.actions__comment_text} text={parsed.text}/>
                                                    )}

                                                    {(parsed.links.length > 0 || parsed.mentions.length > 0) && (
                                                        <div className={styles.actions__comment_chips}>
                                                            {parsed.links.map((link, idx) => (
                                                                <div key={`link-${idx}`} className={styles.actions__comment_linkChip}>
                                                                    <span className={styles.actions__comment_linkChip_icon}>{getLinkIcon(link.url)}</span>
                                                                    <a
                                                                        href={parseUrl(link.url)?.href || link.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className={styles.actions__comment_linkChip_text}
                                                                    >
                                                                        {link.title || link.url}
                                                                    </a>
                                                                    {isOwn && (
                                                                        <ChipMenu
                                                                            onEdit={() => startEditComment(comment, idx)}
                                                                            onRemove={() => handleRemoveCommentLink(comment, idx)}
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))}
                                                            {parsed.mentions.map((mention, idx) => (
                                                                <span key={`mention-${idx}`} className={styles.actions__comment_mentionChip}>
                                                                    <span className={styles.actions__comment_mentionChip_at}>@</span>
                                                                    <span>{mention.name}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
                                                        <div className={styles.actions__attachments}>
                                                            {comment.attachments.map((att: any) => (
                                                                <CommentAttachment key={att.id} attachment={att} />
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className={styles.actions__comment_divider} />

                                                    <div className={styles.actions__comment_footer}>
                                                        <div className={styles.actions__comment_footerLeft}>
                                                            {isOwn && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className={`${styles.actions__comment_action} ${styles.actions__comment_actionDanger}`}
                                                                        title="Видалити коментар"
                                                                        aria-label="Видалити коментар"
                                                                        onClick={() => setCommentToDelete(comment)}
                                                                    >
                                                                        <TrashIcon />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={styles.actions__comment_action}
                                                                        title="Редагувати коментар"
                                                                        aria-label="Редагувати коментар"
                                                                        onClick={() => startEditComment(comment)}
                                                                    >
                                                                        <EditIcon />
                                                                    </button>
                                                                </>
                                                            )}
                                                            <CommentLikeAction
                                                                comment={comment}
                                                                user={user}
                                                                optimisticUser={currentProjectUser}
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className={styles.actions__comment_reply}
                                                            aria-label="Ответить на комментарий"
                                                            aria-expanded={replyingToId === comment.id}
                                                            onClick={() => {
                                                                stopEditComment()
                                                                setReplyingToId((prev) => prev === comment.id ? null : comment.id)
                                                            }}
                                                        >
                                                            Ответить
                                                        </button>
                                                    </div>

                                                    {replyingToId === comment.id && (
                                                        <div className={styles.actions__comment_replyBox}>
                                                            <CommentInput
                                                                projectId={projectId}
                                                                parentCommentId={Number(comment.id)}
                                                                autoExpand
                                                                placeholder="Напишите ответ"
                                                                onSubmit={(payload) => handleCreateComment(payload, Number(comment.id))}
                                                                onCancel={() => setReplyingToId(null)}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            </div>
            <div className={styles.actions__send}>
                <CommentInput projectId={projectId} onSubmit={handleCreateComment} />
            </div>
            <ProjectDeleteModal
                isOpen={!!commentToDelete}
                title="Удалить комментарий?"
                text="Комментарий перенесется в корзину, удалится оттуда через 90 дней."
                onClose={() => setCommentToDelete(null)}
                onConfirm={handleDeleteComment}
            />
        </div>
    )
}
